// The Plan room's typed writes, as the Swift makes them: the beat sheet (`setBeatSheet`, `assign(_:toBeat:)`), board
// positions (`setBoardPoints`, Tidy Up, the first drop, Order Binder by Position = `reorder`), swimlane drops, collections
// (`putSavedSearch`/`removeSavedSearch`), templates (`apply`, `newDocument(from:)`, `copyDocument`, `saveAsTemplate`,
// `researchArrangementID`), journals (New Post, `setMetadata` on a selection then `numberPosts`, the first number,
// `ensureJournalFields`), the status workflow and alternates (`forkFragment`, `forkFolder`, `promote`). Each method is one
// Automerge change per document it touches, with the Swift's (or the app's) commit message; stable ids are derived as the
// Swift derives them (beats `<template>.<slug>`, Research & Notes from the project id), so two devices doing the same
// thing merge.
import * as A from '@automerge/automerge';
import type { FragmentKind, MetadataValue, Placement } from '../contract.ts';
import { between, sequence } from '../automerge/fractionalIndex.ts';
import { ROOT, SwiftReader } from '../automerge/swiftDoc.ts';
import {
  childrenOf, fragmentDocName, isWithinSubtree, metadataScalar, putNode, readMetadata, storedWordTarget, subtree, type EditOptions,
} from '../edit/editProject.ts';
import {
  alternateSets, manuscriptKind, ModelError, registerFragment, StoreEditor, suiteJSON, writeArrangement, writeField, writeFragmentKeys,
  type AlternateSet,
} from '../edit/storeEditor.ts';
import { bool, changeTime, f64, int, str, Writer } from '../edit/typed.ts';
import { tree as buildTree, type ArrangementTreeNode } from '../model/arrangement.ts';
import { readFragment, type Arrangement, type ArrangementNode } from '../model/documents.ts';
import { AlternatesError } from './alternates.ts';
import { BEAT_FIELD, BEAT_SHEET_KEY, beatField, decodeBeatSheet, encodeBeatSheet, type BeatSheet } from './beatSheet.ts';
import { dropChanges, placedPoints, readingOrder, tidyUpChanges, type BoardPoint } from './boardLayout.ts';
import { JOURNAL_STATUS, missingJournalFields, validFirstPostNumber } from './journal.ts';
import { postStart } from '../journal.ts';
import { wordCount } from '../proseMarkdown.ts';
import { BookMatter } from '../bookMatter.ts';
import { COLLECTIONS_KEY, decodeSavedSearch, encodeSavedSearch, type SavedSearch } from './savedSearch.ts';
import { mergedJSON } from '../settingsJSON.ts';
import { lineEdits } from '../lineEdits.ts';
import { parseTags } from '../tags.ts';
import { nextStatus } from './targets.ts';
import {
  DocumentTemplates, findTemplatesFolder, firstScene, SKIPPED_ON_COPY, type BuiltInTemplate, type ProjectTemplate, type TemplateItem,
} from './templates.ts';

/** `FragmentDocument.create`'s keys (`writeFragmentKeys`), then the text, synopsis, notes and metadata given (inside the
 *  document's first change). */
function writeNewFragment(w: Writer, d: Record<string, any>, o: {
  id: string; projectID: string; kind: FragmentKind; title: string; createdAt: Date;
  text?: string; synopsis?: string; notes?: string; metadata?: Record<string, MetadataValue>; includeInCompile?: boolean;
}) {
  writeFragmentKeys(w, d, o);
  for (const key of ['text', 'synopsis', 'notes'] as const) if (o[key]) A.splice(d, [key], 0, 0, o[key]!);
  for (const [key, value] of Object.entries(o.metadata ?? {})) w.put(['metadata'], key, metadataScalar(value));
  if (o.includeInCompile === false) w.put([], 'includeInCompile', bool(false));
}

/** `setText` on a settings text object: made if missing, updated by a minimal diff. `byLines` (`setTextByLines`, as
 *  `setCompilePreset` writes): whole lines replaced, so two devices changing different lines keep both changes. */
function setSettingsText(w: Writer, d: Record<string, any>, map: string, key: string, text: string, byLines = false) {
  w.ensure(['settings', map]);
  const holder = d.settings[map];
  if (typeof holder[key] !== 'string') holder[key] = '';
  const old: string = holder[key];
  if (old === text) return;
  if (!byLines) {
    A.updateText(d as A.Doc<unknown>, ['settings', map, key], text);
    return;
  }
  for (const e of lineEdits(old, text).reverse()) A.splice(d as A.Doc<unknown>, ['settings', map, key], e.start, e.delete, e.insert || undefined);
}

/** `DocumentStore.Placement`, with `.first(parent)`: first inside a folder (null = the top level), as New Post places. */
export type PlanPlacement = Placement | { firstIn: string | null };

/** `MetadataValue ==`. */
function sameValue(a: MetadataValue | undefined, b: MetadataValue | null): boolean {
  if (!a || !b) return !a && !b;
  if (a.type !== b.type) return false;
  if (a.type === 'date') return a.value.getTime() === (b.value as Date).getTime();
  return a.value === b.value;
}

export class PlanEditor extends StoreEditor {
  constructor(docs: Map<string, A.Doc<unknown>>, context: EditOptions) {
    super(docs, context);
  }

  private metadataOf(fragmentID: string): Record<string, MetadataValue> | undefined {
    const doc = this.docs.get(fragmentDocName(fragmentID));
    return doc ? readMetadata(new SwiftReader(doc)) : undefined;
  }

  // MARK: New documents

  /** `DocumentStore.newFragment(in:arrangementID:placement:title:kind:isFolder:text:metadata:)`: the document made with its
   *  text and metadata in one change ("Created"), then placed ("New fragment “…”" / "New folder “…”"). */
  newFragmentWith(o: { arrangementId: string; placement: PlanPlacement; title?: string; kind?: FragmentKind; isFolder?: boolean; text?: string;
    metadata?: Record<string, MetadataValue> }): { fragmentId: string; nodeId: string; changed: string[] } {
    const project = this.project();
    const a = this.arrangement(project, o.arrangementId);
    const title = o.title ?? 'Untitled';
    const kind = o.kind ?? 'prose';
    const isFolder = o.isFolder ?? false;
    const createdAt = this.now();
    const fragmentId = this.id();
    const actor = this.context.newActor?.();
    const doc = A.change(A.init<Record<string, any>>(actor ? { actor } : undefined), { message: 'Created', time: changeTime(createdAt) }, (d) => {
      writeNewFragment(new Writer(d, new SwiftReader(A.init())), d, { id: fragmentId, projectID: project.id, kind, title, createdAt, text: o.text, metadata: o.metadata });
    });
    const name = fragmentDocName(fragmentId);
    this.docs.set(name, doc as A.Doc<unknown>);
    this.written.add(doc);
    const nodeId = this.id();
    const { parent, position } = 'firstIn' in o.placement
      ? { parent: o.placement.firstIn, position: between(undefined, childrenOf(a, o.placement.firstIn)[0]?.position) }
      : this.slot(a, o.placement);
    this.change('project', `New ${isFolder ? 'folder' : 'fragment'} “${title}”`, createdAt, (w) => {
      registerFragment(w, { id: fragmentId, kind, trashed: false, createdAt });
      putNode(w, o.arrangementId, { id: nodeId, fragmentID: fragmentId, isFolder, parentID: parent ?? undefined, position });
    });
    return { fragmentId, nodeId, changed: [name, 'project'] };
  }

  // MARK: Metadata on a selection (BulkMetadata)

  /** `setMetadata(_:to:on:)`: one field on several documents (null clears it), one change each (with `modifiedAt`), skipping
   *  unchanged ones; a journal's posts scheduled or published then take their numbers, oldest first, in one pass.
   *  Returns how many changed and the documents written. */
  setMetadataOn(fragmentIDs: string[], field: string, value: MetadataValue | null): { count: number; changed: string[] } {
    const stored = field === 'word-target' ? storedWordTarget(value) : value;
    const changed: string[] = [];
    let count = 0;
    // A tags field takes its tags as text ("a, b") and stores them tidied; none clears it.
    if (this.project().fields.some((f) => f.id === field && f.type === 'tags')) {
      for (const id of new Set(fragmentIDs)) {
        if (!this.docs.has(fragmentDocName(id))) continue;
        const written = this.setTags(id, value?.type === 'text' ? parseTags(value.value) : [], field);
        if (written.length) { count += 1; changed.push(...written); }
      }
      return { count, changed };
    }
    for (const id of new Set(fragmentIDs)) {
      const current = this.metadataOf(id);
      if (!current || sameValue(current[field], stored)) continue;
      const written = this.updateFragment(id, (w) => {
        if (stored) w.putIfChanged(['metadata'], field, metadataScalar(stored));
        else w.delete(['metadata'], field);
      });
      if (written.length) { count += 1; changed.push(...written); }
    }
    if (count > 0 && field === 'status') for (const n of this.numberPosts(fragmentIDs)) if (!changed.includes(n)) changed.push(n);
    return { count, changed };
  }

  /** `setIncludeInCompile(_:on:)`. */
  setIncludeInCompileOn(fragmentIDs: string[], include: boolean): { count: number; changed: string[] } {
    const changed: string[] = [];
    for (const id of new Set(fragmentIDs)) if (this.docs.has(fragmentDocName(id))) changed.push(...this.setIncludeInCompile(id, include));
    return { count: changed.length, changed };
  }

  /** `stepStatus`: a document's status one step on (or back) through the workflow; null when it can't move. */
  stepStatus(fragmentID: string, forward = true): { status: string; changed: string[] } | null {
    const field = this.project().fields.find((f) => f.id === 'status');
    if (!field) return null;
    const next = nextStatus(field, this.metadataOf(fragmentID)?.status, forward);
    if (!next) return null;
    return { status: next.id, changed: this.setMetadataOn([fragmentID], 'status', { type: 'text', value: next.id }).changed };
  }

  // MARK: Beats

  /** `setBeatSheet` (null removes it), committed as the app does ("Beat sheet: …" / "Remove the beat sheet"): the sheet
   *  as JSON (`putJSON`: what this build can't read is kept; an unreadable stored sheet is never overwritten) and the
   *  Beat field's options brought into line with its beats (keeping the field's order). */
  setBeatSheet(sheet: BeatSheet | null): string[] {
    const project = this.project();
    const stored = project.settings[BEAT_SHEET_KEY];
    const json = mergedJSON(stored, sheet, decodeBeatSheet, 'beat sheet', encodeBeatSheet);
    const message = sheet ? `Beat sheet: ${sheet.name}` : 'Remove the beat sheet';
    return this.change('project', message, this.now(), (w, _r, d) => {
      w.putIfChanged(['settings'], BEAT_SHEET_KEY, str(json));
      if (!sheet) return;
      const field = beatField(sheet);
      const existing = project.fields.find((f) => f.id === BEAT_FIELD);
      if (existing) field.order = existing.order;
      writeField(w, d, field, project);
    }) ? ['project'] : [];
  }

  /** `assign(_:toBeat:)`: scenes to a beat (null: to none). */
  assignToBeat(fragmentIDs: string[], beatID: string | null): string[] {
    return this.setMetadataOn(fragmentIDs, BEAT_FIELD, beatID ? { type: 'text', value: beatID } : null).changed;
  }

  // MARK: The corkboard

  /** `setBoardPoints`: cards moved on a freeform board (null takes a card off, to be laid out again), "Arrange cards". */
  setBoardPoints(arrangementID: string, points: ReadonlyMap<string, BoardPoint | null> | Record<string, BoardPoint | null>): string[] {
    const entries = points instanceof Map ? [...points] : Object.entries(points);
    if (!entries.length) return [];
    const a = this.arrangement(this.project(), arrangementID);
    for (const [id] of entries) if (!a.nodes.has(id)) throw new ModelError('notFound', `node ${id}`);
    return this.change('project', 'Arrange cards', this.now(), (w) => {
      for (const [id, point] of entries) {
        const path = ['arrangements', arrangementID, 'nodes', id];
        if (point) {
          w.putIfChanged(path, 'boardX', f64(point.x));
          w.putIfChanged(path, 'boardY', f64(point.y));
        } else {
          w.delete(path, 'boardX');
          w.delete(path, 'boardY');
        }
      }
    }) ? ['project'] : [];
  }

  /** A card dropped on the freeform board at `point`: the first drop pins every card where it's shown (`shown`). */
  dropCard(arrangementID: string, nodeID: string, point: BoardPoint, shown: ReadonlyMap<string, BoardPoint>): string[] {
    const a = this.arrangement(this.project(), arrangementID);
    return this.setBoardPoints(arrangementID, dropChanges(nodeID, point, shown, placedPoints(a.nodes.values())));
  }

  /** Tidy Up: the cards taken off the board. */
  tidyUp(arrangementID: string, nodeIDs: Iterable<string>): string[] {
    return this.setBoardPoints(arrangementID, tidyUpChanges(nodeIDs));
  }

  /** `reorder`: siblings put in the order given with fresh positions ("Order cards by position"); nothing unless they
   *  share one parent. */
  reorder(nodeIDs: string[], arrangementID: string): string[] {
    const a = this.arrangement(this.project(), arrangementID);
    const parents = new Set(nodeIDs.filter((id) => a.nodes.has(id)).map((id) => a.nodes.get(id)!.parentID ?? null));
    if (parents.size !== 1) return [];
    const positions = sequence(nodeIDs.length);
    return this.change('project', 'Order cards by position', this.now(), (w) => {
      nodeIDs.forEach((id, i) => { if (a.nodes.has(id)) w.putIfChanged(['arrangements', arrangementID, 'nodes', id], 'position', str(positions[i])); });
    }) ? ['project'] : [];
  }

  /** Order Binder by Position: the cards' folder takes their reading order on the board. */
  orderByBoard(arrangementID: string, points: ReadonlyMap<string, BoardPoint>): string[] {
    return this.reorder(readingOrder(points), arrangementID);
  }

  /** `move(_:in:arrangementID:toParent:index:)`: one card to `index` among `parentID`'s other children ("Moved"); a move
   *  into its own subtree is refused silently, as the Swift does. */
  moveCard(nodeID: string, arrangementID: string, parentID: string | null, index: number): string[] {
    const a = this.arrangement(this.project(), arrangementID);
    if (!a.nodes.has(nodeID)) throw new ModelError('notFound', `node ${nodeID}`);
    if (isWithinSubtree(a, parentID, nodeID)) return [];
    const siblings = childrenOf(a, parentID).filter((n) => n.id !== nodeID);
    const clamped = Math.max(0, Math.min(index, siblings.length));
    const position = between(clamped > 0 ? siblings[clamped - 1].position : undefined, siblings[clamped]?.position);
    return this.change('project', 'Moved', this.now(), (w) => {
      const path = ['arrangements', arrangementID, 'nodes', nodeID];
      w.putOptional(path, 'parent', parentID);
      w.put(path, 'position', str(position));
    }) ? ['project'] : [];
  }

  /** A swimlane drop: the field set to the lane's value (null: the "No …" lane clears it). */
  dropOnLane(fragmentIDs: string[], field: string, laneValue: string | null): string[] {
    return this.setMetadataOn(fragmentIDs, field, laneValue ? { type: 'text', value: laneValue } : null).changed;
  }

  // MARK: Collections

  /** `putSavedSearch`, committed as the app does ("Save collection “…”"). */
  putSavedSearch(search: SavedSearch): string[] {
    const project = this.project();
    const all = project.settings[COLLECTIONS_KEY] as Record<string, unknown> | undefined;
    const stored = all && typeof all === 'object' ? all[search.id] : undefined;
    const json = mergedJSON(stored, search, decodeSavedSearch, 'collection', encodeSavedSearch);
    return this.change('project', `Save collection “${search.name}”`, this.now(), (w) => {
      w.putIfChanged(['settings', COLLECTIONS_KEY], search.id, str(json));
    }) ? ['project'] : [];
  }

  /** `removeSavedSearch` ("Delete collection “…”"). */
  removeSavedSearch(id: string): string[] {
    const project = this.project();
    const all = project.settings[COLLECTIONS_KEY];
    if (!all || typeof all !== 'object' || !(id in (all as object))) return [];
    let name = '';
    try { name = (JSON.parse((all as Record<string, string>)[id]) as { name?: string }).name ?? ''; } catch { /* unnamed */ }
    return this.change('project', `Delete collection “${name}”`, this.now(), (w) => w.delete(['settings', COLLECTIONS_KEY], id)) ? ['project'] : [];
  }

  /** `ProjectDocument.setCompilePreset`: a project's own format settings (Publish ▸ Adjust…), written a line at a time;
   *  null removes them. The caller checks the text reads as a preset first. */
  setCompilePreset(id: string, yaml: string | null): string[] {
    const message = yaml === null ? 'Delete format settings' : 'Adjust format settings';
    return this.change('project', message, this.now(), (w, _r, d) => {
      if (yaml === null) w.delete(['settings', 'compilePresets'], id);
      else setSettingsText(w, d, 'compilePresets', id, yaml, true);
    }) ? ['project'] : [];
  }

  // MARK: Templates

  private sectionOptions() {
    return this.project().fields.find((f) => f.id === 'section-type')?.options ?? [];
  }

  /** `apply(_:to:)`: builds a template's documents in a new project — manuscript items with their Section types (a
   *  journal's posts as drafts), research items in Research & Notes, the manuscript target and compile presets ("Start
   *  from the … template") — and returns the node of the scene to open. */
  applyTemplate(template: ProjectTemplate): string | null {
    const project = this.project();
    const options = this.sectionOptions();
    const manuscript = project.arrangements.find((a) => a.role === 'manuscript');
    const journal = project.projectType === 'journal';
    const build = (items: TemplateItem[], parent: string | null, arrangementID: string, kind: FragmentKind) => {
      for (const item of items) {
        const metadata: Record<string, MetadataValue> = {};
        const option = item.sectionType !== undefined ? options.find((o) => o.name === item.sectionType) : undefined;
        if (option) metadata['section-type'] = { type: 'text', value: option.id };
        if (journal && !item.folder && arrangementID === manuscript?.id) metadata.status = { type: 'text', value: JOURNAL_STATUS.draft };
        const fragmentKind: FragmentKind = item.folder ? (kind === 'screenplay' ? 'screenplay' : 'prose') : kind;
        const made = this.newFragmentWith({ arrangementId: arrangementID, placement: { inside: parent }, title: item.title, kind: fragmentKind,
          isFolder: item.folder, text: item.text, metadata });
        if (item.notes !== '') this.writeFragment(made.fragmentId, { notes: item.notes }, 'Template notes');
        build(item.children, made.nodeId, arrangementID, kind);
      }
    };
    let opening: string | null = null;
    if (manuscript) {
      build(template.manuscript, null, manuscript.id, manuscriptKind(project.projectType));
      const path = firstScene(template.manuscript);
      if (path) {
        let level: ArrangementTreeNode[] = buildTree(this.arrangement(this.project(), manuscript.id));
        path.forEach((index, depth) => {
          if (index >= level.length) return;
          if (depth === path.length - 1) opening = level[index].node.id;
          else level = level[index].children;
        });
      }
    }
    if (template.research.length) build(template.research, null, this.researchArrangementID(), 'note');
    const now = this.project();
    this.change('project', `Start from the ${template.name} template`, this.now(), (w, _r, d) => {
      if (template.manuscriptWords !== undefined) {
        // setWritingTargets: the manuscript's words, the rest as they were.
        const path = ['settings', 'targets'];
        const words = template.manuscriptWords;
        if (words > 0) w.putIfChanged(path, 'manuscriptWords', int(words)); else w.delete(path, 'manuscriptWords');
        const session = now.targets.sessionWords;
        if (session !== undefined && session > 0) w.putIfChanged(path, 'sessionWords', int(session)); else w.delete(path, 'sessionWords');
        w.putIfChanged(path, 'countAllText', { t: 'boolean', v: now.targets.countAllText });
      }
      for (const [id, yaml] of Object.entries(template.presets)) setSettingsText(w, d, 'compilePresets', id, yaml, true);
    });
    return opening;
  }

  /** `newDocument(from:in:arrangementID:placement:kind:)`: a built-in template's document (and its children) placed as
   *  asked; returns its node. */
  newDocumentFromTemplate(template: Pick<BuiltInTemplate, 'item'>, arrangementID: string, placement: PlanPlacement, kind: FragmentKind,
    top: Record<string, MetadataValue> = {}): string {
    const options = this.sectionOptions();
    const build = (item: TemplateItem, placement: PlanPlacement, isTop = false): string => {
      const metadata: Record<string, MetadataValue> = isTop ? { ...top } : {};
      const option = item.sectionType !== undefined ? options.find((o) => o.name === item.sectionType) : undefined;
      if (option) metadata['section-type'] = { type: 'text', value: option.id };
      const made = this.newFragmentWith({ arrangementId: arrangementID, placement, title: item.title, kind, isFolder: item.folder, text: item.text, metadata });
      if (item.notes !== '') this.writeFragment(made.fragmentId, { notes: item.notes }, 'Template notes');
      for (const child of item.children) build(child, { inside: made.nodeId });
      return made.nodeId;
    };
    return build(template.item, placement, true);
  }

  /** `templatesFolder(in:)`: the Templates folder in Research & Notes, if the project has one. */
  templatesFolder(): { arrangementID: string; nodeID: string } | null {
    const research = this.project().arrangements.find((a) => a.role === 'reference');
    if (!research) return null;
    const folder = findTemplatesFolder(buildTree(research), (id) => this.metadataOf(id));
    return folder ? { arrangementID: research.id, nodeID: folder.node.id } : null;
  }

  /** `customTemplates(in:)`: the writer's own templates, the top-level items in the Templates folder. */
  customTemplates(): { nodeID: string; title: string }[] {
    const folder = this.templatesFolder();
    if (!folder) return [];
    const research = this.arrangement(this.project(), folder.arrangementID);
    const node = buildTree(research).find((t) => t.node.id === folder.nodeID);
    return (node?.children ?? []).map((c) => ({
      nodeID: c.node.id,
      title: (c.node.fragmentID !== undefined ? this.titleOf(c.node.fragmentID) : undefined) ?? c.node.title ?? 'Untitled',
    }));
  }

  private titleOf(fragmentID: string): string | undefined {
    const doc = this.docs.get(fragmentDocName(fragmentID));
    return doc ? new SwiftReader(doc).string(ROOT, 'title') ?? '' : undefined;
  }

  /** `copyDocument`: a document and everything in it copied to `placement` — text, synopsis, notes and fields; never
   *  comments, history, photos or the markers of special pages. Returns the copy's node. */
  copyDocument(sourceNodeID: string, sourceArrangementID: string, arrangementID: string, placement: PlanPlacement, kind?: FragmentKind,
    top: Record<string, MetadataValue> = {}): string {
    const source = this.arrangement(this.project(), sourceArrangementID);
    // A document with documents under it stays one in a journal (a post and its sections); elsewhere it's a folder.
    const journal = this.project().projectType === 'journal';
    const find = (nodes: ArrangementTreeNode[]): ArrangementTreeNode | null => {
      for (const n of nodes) { if (n.node.id === sourceNodeID) return n; const hit = find(n.children); if (hit) return hit; }
      return null;
    };
    const root = find(buildTree(source));
    if (!root) throw new ModelError('notFound', `template ${sourceNodeID}`);
    const copy = (node: ArrangementTreeNode, placement: PlanPlacement, isTop = false): string => {
      let original = null;
      if (node.node.fragmentID !== undefined) {
        const doc = this.docs.get(fragmentDocName(node.node.fragmentID));
        if (!doc) throw new ModelError('notFound', `fragment ${node.node.fragmentID}`);
        original = readFragment(doc);
      }
      const metadata = { ...Object.fromEntries(Object.entries(original?.metadata ?? {}).filter(([k]) => !SKIPPED_ON_COPY.includes(k))), ...(isTop ? top : {}) };
      const made = this.newFragmentWith({
        arrangementId: arrangementID, placement, title: original?.title ?? node.node.title ?? 'Untitled', kind: kind ?? original?.kind ?? 'prose',
        isFolder: node.node.isFolder || (!journal && node.children.length > 0), text: original?.text ?? '', metadata,
      });
      if (original) this.writeFragment(made.fragmentId, { synopsis: original.synopsis, notes: original.notes }, `Copied from “${original.title}”`);
      for (const child of node.children) copy(child, { inside: made.nodeId });
      return made.nodeId;
    };
    return copy(root, placement, true);
  }

  /** `saveAsTemplate`: a copy in the Templates folder (made on first use, marked `document-template: folder`). */
  saveAsTemplate(nodeID: string, arrangementID: string): string {
    let folder = this.templatesFolder();
    if (!folder) {
      const researchID = this.researchArrangementID();
      const made = this.newFragmentWith({ arrangementId: researchID, placement: { inside: null }, title: DocumentTemplates.folderTitle, kind: 'note',
        isFolder: true, metadata: { [DocumentTemplates.field]: { type: 'text', value: DocumentTemplates.folderMarker } } });
      folder = { arrangementID: researchID, nodeID: made.nodeId };
    }
    return this.copyDocument(nodeID, arrangementID, folder.arrangementID, { inside: folder.nodeID });
  }

  // MARK: Journals

  /** New Post (`DocumentStore.newPost`): at the top of its folder (the top level by default), as a draft, of the project's
   *  scene kind, holding its own copy of the journal's opening and sign-off (`postStart`, marked `own-post-matter`) when
   *  the journal has either; `caret` is where Write puts the caret (Unicode scalars; `caretUTF16` the same in UTF-16), null
   *  when the post starts empty; `words` the copy's words, which the day's count passes over. */
  newPost(o: { title?: string; parentID?: string | null; placement?: PlanPlacement } = {}):
    { fragmentId: string; nodeId: string; changed: string[]; caret: number | null; caretUTF16: number | null; words: number } {
    const project = this.project();
    const manuscript = project.arrangements.find((a) => a.role === 'manuscript');
    if (!manuscript) throw new ModelError('notFound', 'the manuscript');
    const matter = this.postMatter();
    const start = postStart(matter.opening, matter.signOff);
    const metadata: Record<string, MetadataValue> = { status: { type: 'text', value: JOURNAL_STATUS.draft } };
    if (start) metadata['own-post-matter'] = { type: 'boolean', value: true };
    const kind = manuscriptKind(project.projectType);
    const made = this.newFragmentWith({ arrangementId: manuscript.id, placement: o.placement ?? { firstIn: o.parentID ?? null },
      title: o.title ?? 'Untitled', kind, text: start?.text, metadata });
    const caretUTF16 = start ? [...start.text].slice(0, start.caret).join('').length : null;
    return { ...made, caret: start?.caret ?? null, caretUTF16, words: start ? wordCount(start.text, kind) : 0 };
  }

  /** `postMatter(in:)`: the journal's Opening and Sign-off as stored — the documents marked as them outside the Trash (their
   *  own ids first), null while Include in Compile is off, as compile leaves them out. */
  private postMatter(): { opening: string | null; signOff: string | null } {
    const project = this.project();
    if (project.projectType !== 'journal') return { opening: null, signOff: null };
    const arrangements = project.arrangements.filter((a) => a.role !== 'trash');
    const placed = new Set(arrangements.flatMap((a) => [...a.nodes.values()].flatMap((n) => (n.fragmentID ? [n.fragmentID] : []))));
    const ordered = (a: Arrangement): string[] => {
      const out: string[] = [];
      const walk = (nodes: ArrangementTreeNode[]) => { for (const n of nodes) { if (n.node.fragmentID) out.push(n.node.fragmentID); walk(n.children); } };
      walk(buildTree(a));
      return out;
    };
    const text = (kind: 'post-opening' | 'post-sign-off'): string | null => {
      const own = BookMatter.stableID(kind, project.id);
      const candidates = [own, ...arrangements.filter((a) => a.role !== 'manuscript').flatMap(ordered).filter((id) => id !== own)];
      for (const id of candidates) {
        const doc = placed.has(id) ? this.docs.get(fragmentDocName(id)) : undefined;
        if (!doc) continue;
        const fragment = readFragment(doc);
        const marker = fragment.metadata[BookMatter.field];
        if (marker?.type !== 'text' || marker.value !== kind) continue;
        return fragment.includeInCompile ? fragment.text : null;
      }
      return null;
    };
    return { opening: text('post-opening'), signOff: text('post-sign-off') };
  }

  /** `setFirstPostNumber` ("Set the first post number"): above 1 stored, 1 or null cleared; at the limit refused. */
  setFirstPostNumber(n: number | null): string[] {
    const value = validFirstPostNumber(n);
    return this.change('project', 'Set the first post number', this.now(), (w, r) => {
      if (value !== null) w.putIfChanged(['settings'], 'firstPostNumber', int(value));
      else {
        const settings = r.map(ROOT, 'settings');
        if (settings && r.get(settings, 'firstPostNumber')?.[0] === 'int') w.delete(['settings'], 'firstPostNumber');
      }
    }) ? ['project'] : [];
  }

  /** `ensureJournalFields` (the app commits it with "Add standard fields"): a journal's post fields, when missing. */
  ensureJournalFields(message = 'Add standard fields'): string[] {
    const project = this.project();
    if (project.projectType !== 'journal') return [];
    const fields = missingJournalFields(project.fields.map((f) => f.id));
    if (!fields.length) return [];
    return this.change('project', message, this.now(), (w, _r, d) => { for (const f of fields) writeField(w, d, f, project); }) ? ['project'] : [];
  }

  // MARK: Alternates

  private putSet(w: Writer, set: AlternateSet) {
    w.putIfChanged(['settings', 'alternates'], set.id, str(suiteJSON(set)));
  }

  /** `copyFragment`: text, synopsis, notes, fields and compile setting in a new document ("Alternate of “…”"), placed
   *  nowhere; returns its id and the registration to write in the caller's project change. */
  private copyFragment(id: string, date: Date): { id: string; register: (w: Writer) => void } {
    const doc = this.docs.get(fragmentDocName(id));
    if (!doc) throw new ModelError('notFound', `fragment ${id}`);
    const source = readFragment(doc);
    const project = this.project();
    const copyID = this.id();
    const actor = this.context.newActor?.();
    const copy = A.change(A.init<Record<string, any>>(actor ? { actor } : undefined), { message: `Alternate of “${source.title}”`, time: changeTime(date) }, (d) => {
      writeNewFragment(new Writer(d, new SwiftReader(A.init())), d, {
        id: copyID, projectID: project.id, kind: source.kind, title: source.title, createdAt: date, text: source.text, synopsis: source.synopsis,
        notes: source.notes, metadata: source.metadata, includeInCompile: source.includeInCompile,
      });
    });
    this.docs.set(fragmentDocName(copyID), copy as A.Doc<unknown>);
    this.written.add(copy);
    return { id: copyID, register: (w) => registerFragment(w, { id: copyID, kind: source.kind, trashed: false, createdAt: date }) };
  }

  /** `forkFragment`: the scene at a node forked into a new alternate `name` (an editable copy, not yet current), joining
   *  the node's set or starting one ("New alternate “…”"). */
  forkFragment(nodeID: string, arrangementID: string, name: string, originalName = 'Original'): AlternateSet {
    const project = this.project();
    const node = this.arrangement(project, arrangementID).nodes.get(nodeID);
    if (!node || node.fragmentID === undefined) throw new AlternatesError('notFound', 'the scene');
    const fragmentID = node.fragmentID;
    const date = this.now();
    const found = alternateSets(project).find((s) => s.kind === 'fragment' && s.nodeID === nodeID && s.arrangementID === arrangementID);
    const set: AlternateSet = found ?? {
      id: this.id(), kind: 'fragment', title: this.titleOf(fragmentID) ?? '',
      members: [{ id: this.id(), name: originalName, createdAt: date, fragmentID }], currentMemberID: '', arrangementID, nodeID,
    };
    if (set.currentMemberID === '') set.currentMemberID = set.members[0].id;
    const copy = this.copyFragment(fragmentID, date);
    set.members.push({ id: this.id(), name, createdAt: date, fragmentID: copy.id });
    this.change('project', `New alternate “${name}”`, date, (w) => { copy.register(w); this.putSet(w, set); });
    return set;
  }

  /** `forkFolder`: a folder's document and everything under it copied into a new alternate arrangement
   *  "<folder> — <name>" ("New alternate “…” of “…”"). */
  forkFolder(nodeID: string, arrangementID: string, name: string, originalName = 'Original'): AlternateSet {
    const project = this.project();
    const source = this.arrangement(project, arrangementID);
    const folder = source.nodes.get(nodeID);
    if (!folder || !(folder.isFolder || [...source.nodes.values()].some((n) => n.parentID === nodeID))) throw new AlternatesError('notAFolder');
    const title = (folder.fragmentID !== undefined ? this.titleOf(folder.fragmentID) : undefined) ?? folder.title ?? 'Folder';
    const date = this.now();
    const found = alternateSets(project).find((s) => s.kind === 'folder' && s.nodeID === nodeID && s.arrangementID === arrangementID);
    const set: AlternateSet = found ?? {
      id: this.id(), kind: 'folder', title, members: [{ id: this.id(), name: originalName, createdAt: date }], currentMemberID: '', arrangementID, nodeID,
    };
    if (set.currentMemberID === '') set.currentMemberID = set.members[0].id;
    const holder = this.id();
    const writes: ((w: Writer) => void)[] = [(w) => writeArrangement(w, project, holder, `${title} — ${name}`, 'alternate')];
    const ids = new Map<string, string>();
    for (const node of subtree(source, nodeID)) {
      const copied = node.fragmentID !== undefined ? this.copyFragment(node.fragmentID, date) : null;
      const newID = this.id();
      ids.set(node.id, newID);
      const parent = node.id === nodeID ? undefined : node.parentID !== undefined ? ids.get(node.parentID) : undefined;
      const placed: ArrangementNode = { id: newID, fragmentID: copied?.id, title: node.title, isFolder: node.isFolder, parentID: parent,
        position: parent === undefined ? sequence(1)[0] : node.position };
      if (copied) writes.push(copied.register);
      writes.push((w) => putNode(w, holder, placed));
    }
    set.members.push({ id: this.id(), name, createdAt: date, arrangementID: holder, rootNodeID: ids.get(nodeID) });
    this.change('project', `New alternate “${name}” of “${title}”`, date, (w) => { for (const write of writes) write(w); this.putSet(w, set); });
    return set;
  }

  /** `promote`: makes a member current; the one it replaces is archived in its place, never deleted ("Promote alternate
   *  “…”"). A scene set repoints the node; a folder set swaps the subtrees (and the folder's own document) with the
   *  member's holder arrangement, renamed after the version it now archives; an arrangement set swaps roles. */
  promote(memberID: string, setID: string): string[] {
    const project = this.project();
    const set = alternateSets(project).find((s) => s.id === setID);
    if (!set) return [];
    const incoming = set.members.findIndex((m) => m.id === memberID);
    const outgoing = set.members.findIndex((m) => m.id === set.currentMemberID);
    if (incoming < 0 || outgoing < 0 || incoming === outgoing) return [];
    const writes: ((w: Writer) => void)[] = [];
    switch (set.kind) {
      case 'fragment': {
        const { arrangementID, nodeID } = set;
        const node = arrangementID !== undefined && nodeID !== undefined ? this.arrangement(project, arrangementID).nodes.get(nodeID) : undefined;
        if (!node || arrangementID === undefined) throw new AlternatesError('notFound', 'the scene\'s place');
        writes.push((w) => putNode(w, arrangementID, { ...node, fragmentID: set.members[incoming].fragmentID }));
        break;
      }
      case 'folder': {
        const { arrangementID, nodeID } = set;
        const holderID = set.members[incoming].arrangementID;
        if (arrangementID === undefined || nodeID === undefined || holderID === undefined) throw new AlternatesError('notFound', 'the folder\'s place');
        const source = this.arrangement(project, arrangementID);
        const holder = this.arrangement(project, holderID);
        // The member's recorded root; sets made before it was recorded only when the holder has one top-level item.
        const roots = [...holder.nodes.values()].filter((n) => n.parentID === undefined);
        const recordedID = set.members[incoming].rootNodeID;
        const root = recordedID !== undefined ? holder.nodes.get(recordedID) : roots.length === 1 ? roots[0] : undefined;
        const folder = source.nodes.get(nodeID);
        if (!folder || !root) throw new AlternatesError('notFound', 'the folder');
        const outgoingChildren = [...source.nodes.values()].filter((n) => n.parentID === nodeID);
        const incomingChildren = [...holder.nodes.values()].filter((n) => n.parentID === root.id);
        const outgoingNodes = subtree(source, nodeID).slice(1);
        const incomingNodes = subtree(holder, root.id).slice(1);
        writes.push((w) => {
          for (const n of outgoingChildren) for (const x of subtree(source, n.id)) w.delete(['arrangements', arrangementID, 'nodes'], x.id);
          for (const n of incomingChildren) for (const x of subtree(holder, n.id)) w.delete(['arrangements', holderID, 'nodes'], x.id);
          for (const n of incomingNodes) putNode(w, arrangementID, n.parentID === root.id ? { ...n, parentID: nodeID } : n);
          for (const n of outgoingNodes) putNode(w, holderID, n.parentID === nodeID ? { ...n, parentID: root.id } : n);
          putNode(w, arrangementID, { ...folder, fragmentID: root.fragmentID });
          putNode(w, holderID, { ...root, fragmentID: folder.fragmentID });
          w.putIfChanged(['arrangements', holderID], 'name', str(`${set.title} — ${set.members[outgoing].name}`));
        });
        set.members[outgoing] = { ...set.members[outgoing], arrangementID: holderID, rootNodeID: root.id };
        const { arrangementID: _a, rootNodeID: _r, ...rest } = set.members[incoming];
        set.members[incoming] = rest;
        break;
      }
      case 'arrangement': {
        const incomingID = set.members[incoming].arrangementID, outgoingID = set.members[outgoing].arrangementID;
        if (incomingID === undefined || outgoingID === undefined) throw new AlternatesError('notFound', 'the arrangement');
        this.arrangement(project, incomingID);
        this.arrangement(project, outgoingID);
        writes.push((w) => {
          w.putIfChanged(['arrangements', incomingID], 'role', str(set.role ?? 'manuscript'));
          w.putIfChanged(['arrangements', outgoingID], 'role', str('alternate'));
        });
        break;
      }
    }
    set.currentMemberID = memberID;
    return this.change('project', `Promote alternate “${set.members[incoming].name}”`, this.now(), (w) => {
      for (const write of writes) write(w);
      this.putSet(w, set);
    }) ? ['project'] : [];
  }
}
